import { graphql } from "gatsby";
import React, { useContext, useEffect } from "react";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import { toTextBlock } from "../lib/helpers";
import {
  GetStarted,
  HubspotWebform,
  PageHeader,
  SectionWrapper,
} from "../components/sections";
import PortableText from "../components/portableText";
import { Button } from "../components/button";
import { SiteContext } from "../components/global/site-context";
import { BackToZone } from "../components/back-to-zone";
import { CardGrid } from "../components/card/card-grid";
import { suggest } from "../lib/suggestions";
import { TableOfContentsSection } from "../components/playbook-page";
import { ImageBuilder } from "../components/global/image-builder";
import { ProductDisclaimer } from "../components/product-disclaimer";

const TEASER_IMAGE_HEIGHT = 220;

// DE_MidForm_Role in HubSpot
export const HUBSPOT_ASSET_FORM_ID = "793d54cc-0aa0-4f39-92ab-9aaf08b9c915";

export const query = graphql`
  query AssetTemplateQuery($id: String!) {
    doc: sanityAsset(id: { eq: $id }) {
      _type
      _id
      status
      _updatedAt
      _createdAt
      publishedAt
      title
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      mainImage {
        ...SanityImage
      }
      _rawBody(resolveReferences: { maxDepth: 12 })
      file {
        asset {
          url
          originalFilename
        }
      }
      externalUrl
      formId
      categories {
        _id
      }
      slug {
        current
      }
      graphic {
        ...SanityImageSimple
      }
    }
    recentPosts: allSanityPost(
      limit: 3
      sort: { fields: [publishedAt], order: [DESC] }
      filter: { slug: { current: { ne: null } }, isPublished: { eq: true } }
    ) {
      edges {
        node {
          ...cardNode
        }
      }
    }
  }
`;

const AssetTemplate = ({ data, pageContext }) => (
  <LayoutContainer doc={data.doc}>
    <PageHeadMeta doc={data.doc} />
    {data.doc && <AssetPage {...data.doc} suggest={suggest(data)} gatedMode={pageContext.gatedMode} />}
  </LayoutContainer>
);

export default AssetTemplate;

const AssetPage = (props) => {
  const {
    title,
    file,
    externalUrl,
    formId,
    suggest,
    slug,
    _rawBody,
    _rawExcerpt,
    gatedMode,
    categories,
    _createdAt,
    publishedAt,
  } = props;

  const site = useContext(SiteContext);
  const assetUrl = file?.asset?.originalFilename
    ? `${file?.asset?.url}/${file?.asset?.originalFilename}`
    : externalUrl;

  useEffect(() => {
    if (!formId && !assetUrl) return;

    site.metrics.analytics.once("ready", () => {
      site.metrics.logEvent({
        category: "passive",
        action: formId
          ? "asset-form-view"
          : assetUrl
            ? "asset-download-view"
            : null,
        label: slug.current,
      });
    });
  }, []);

  return (
    <>
      {assetUrl ? (
        <SectionWrapper container="large">
          {site.isActive("resourceHub") && (
            <BackToZone to="/resources/" text={site.token("backToResources")} />
          )}
          <ProductDisclaimer
            categories={categories}
            _createdAt={_createdAt}
            publishedAt={publishedAt}
          />
          <DownloadableAsset
            title={title}
            _rawBody={_rawBody}
            mainImage={props.mainImage}
            formId={formId}
            slug={slug}
            assetUrl={assetUrl}
            gatedMode={gatedMode}
          />
        </SectionWrapper>
      ) : (
        <>
          <SectionWrapper
            {...{
              noHeaders: true,
              background: "lavenderUltraLight",
              name: "header",
              container: "large",
            }}
          >
            <div className="md:w-2/3">
              <BackToZone to="/resources/" text={site.token("backToResources")} />
              <PageHeader
                section={{
                  _rawSummary: toTextBlock(title, "bigTitle").concat(
                    _rawExcerpt || toTextBlock("Missing excerpt", "p")
                  ),
                }}
              />
              <ProductDisclaimer
                categories={categories}
                _createdAt={_createdAt}
                publishedAt={publishedAt}
              />
            </div>
          </SectionWrapper>
          <SectionWrapper
            padding="tight"
            constrainImages={true}
            rounded={true}
            container="large"
          >
            <TableOfContentsSection {...{ _rawBody }} />
          </SectionWrapper>
        </>
      )}

      {site.isActive("suggestions") && (
        <SectionWrapper
          header={site.token("resourcesSuggestions")}
          anchor="more"
          background="lavenderUltraLight"
          container="large"
        >
          <CardGrid
            nodes={suggest.slice(0, 3)}
            showSubTags={false}
            showExtended={false}
            browseMoreHref="/resources/"
            browseMoreText="See all resources"
            makeThumbnail={true}
            hideTitles={true}
            maxColumns={3}
            showSummary={true}
            panels={true}
            attribution={{ action: "suggestion" }}
          />
        </SectionWrapper>
      )}
      <SectionWrapper container="large" background="mint" name="getStarted">
        <GetStarted />
      </SectionWrapper>
    </>
  );
};

const DownloadableAsset = ({
  title,
  _rawBody,
  mainImage,
  formId,
  slug,
  assetUrl,
  gatedMode
}) => {
  const handleFormSubmit = () => {
    if (assetUrl) {
      // Trigger the PDF download after form submission
      window.open(assetUrl, '_blank');
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-0 md:gap-24">
      <div>
        <h1 className="text-2xl md:text-5xl font-bold tracking-tight mb-4">
          {title}
        </h1>
        <PortableText blocks={_rawBody} className="mb-12" />
      </div>
      <div className="mb-8 md:mt-20 order-first lg:order-last">
        {(assetUrl && (formId || gatedMode)) ? (
          <HubspotWebform
            section={{ formId: formId || HUBSPOT_ASSET_FORM_ID }}
            onFormSubmitted={handleFormSubmit}
          />
        ) : (
          assetUrl && (
            <a
              href={assetUrl}
              target="_blank"
              className="p-3 pb-8 bg-dark-5 hover:bg-dark-10 rounded-lg flex flex-col justify-center items-center"
            >
              {mainImage && (
                <ImageBuilder
                  image={mainImage}
                  height={TEASER_IMAGE_HEIGHT}
                  alt={title}
                  className="mb-8 rounded-lg"
                />
              )}
              <Button
                text="Download Now"
                bgColor="bg-green-emerald"

                metrics={{
                  action: "asset-download",
                  label: slug.current,
                }}
                attributes={{ target: "_blank" }}
                noLink={true}
              />
            </a>
          )
        )}
      </div>
    </div>
  );
};
